import { barMenu, cooldownOf, drinkDef, drinkPrice, hasDrink, isDrunk, orderRefusal, slotsUsed, type BarState, type DrinkId } from '../engine/bar';
import type { GameSnapshot } from '../engine/casino';
import { floorDef } from '../engine/floors';
import { money } from '../format';
import type { GameActions } from '../useGame';
import { MenuFrame } from './MenuFrame';
import { useEffect, useRef, useState } from 'react';
import { DrinkIcon } from './DrinkIcon';

/** How long the tip-and-drain plays (matches .drink-icon.drinking in screens.css). */
const DRINK_MS = 1500;
import { BuzzMeter } from './parts';

function lastsLabel(lasts: number | 'once' | 'instant'): string {
  if (lasts === 'instant') return 'right away';
  if (lasts === 'once') return 'one play';
  return `${lasts} plays`;
}

/** How much longer a drink you have going lasts, in words (user,
 *  2026-09-27: show plays left on active drinks). One-shots wait for their
 *  moment instead of counting plays. */
function timeLeft(bar: BarState, id: DrinkId): string | null {
  const d = bar.drinks.find((x) => x.id === id);
  if (!d) return null;
  if (d.playsLeft !== null) return `${d.playsLeft} ${d.playsLeft === 1 ? 'play' : 'plays'} left`;
  return id === 'lager' ? 'Waiting for a loss' : 'On your next play';
}

export function Bar({ snapshot, actions }: { snapshot: GameSnapshot; actions: GameActions }) {
  const run = snapshot.run!;
  const bar = run.bar;
  const multiplier = floorDef(run.floor).betMultiplier;
  const menu = barMenu(run.floor, run.perks.recipes);
  const atTable = !!snapshot.activeGame;
  const toGo = bar.soberEvery - (bar.soberProgress ?? 0);

  // Ordering plays the drink: its glass tips and drains (user, 2026-09-27).
  // Only when the order actually went through (the drinks-ordered count
  // rose), not when the bar refused it.
  const tapped = useRef<DrinkId | null>(null);
  const ordered = run.stats.drinksOrdered;
  const lastOrdered = useRef(ordered);
  const [drinking, setDrinking] = useState<{ id: DrinkId; key: number } | null>(null);
  useEffect(() => {
    if (ordered > lastOrdered.current && tapped.current) setDrinking({ id: tapped.current, key: Date.now() });
    lastOrdered.current = ordered;
  }, [ordered]);
  useEffect(() => {
    if (!drinking) return;
    const t = setTimeout(() => setDrinking(null), DRINK_MS);
    return () => clearTimeout(t);
  }, [drinking]);

  return (
    <MenuFrame snapshot={snapshot} actions={actions} title={atTable ? 'The Waiter' : 'The Bar'} backLabel={atTable ? 'Back to the table' : 'Back to the floor'}>
      <section className="bar-status">
        <BuzzMeter bar={bar} />
        <div className="block paper glasses">
          <div className="label">Your drinks · {slotsUsed(bar)} of {bar.slots} hands</div>
          {bar.drinks.length === 0 ? (
            <p className="muted-line">Nothing going.</p>
          ) : (
            <ul className="drink-chips">
              {bar.drinks.map((d) => (
                <li key={d.id}>
                  <span className="chip-name"><DrinkIcon id={d.id} size={22} empty />{drinkDef(d.id).name}</span>
                  <span>{timeLeft(bar, d.id)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="muted-line">
            {bar.buzz > 0 ? `Buzz drops 1 in ${toGo} ${toGo === 1 ? 'play' : 'plays'}.` : 'Sober.'}
            {isDrunk(bar) ? ' Drunk: drinks cost 50% more.' : ''}
          </p>
        </div>
      </section>
      <ul className="menu-list bar-menu" aria-label="Drinks">
        {menu.map((def, i) => {
          const price = drinkPrice(def, multiplier, bar);
          const refusal = orderRefusal(def, bar, price, run.bankroll);
          const confirming = snapshot.barConfirm === def.id;
          const state = hasDrink(bar, def.id)
            ? `Going · ${timeLeft(bar, def.id)}`
            : refusal === 'cooldown'
              ? `Sold out · back in ${cooldownOf(bar, def.id)}`
              : refusal === 'money'
                ? "Can't afford"
                : refusal === 'slots'
                  ? 'Hands full'
                  : 'Ready';
          const available = refusal === null;
          return (
            <li key={def.id}>
              <button className={`menu-item${available ? '' : ' off'}${confirming ? ' confirming' : ''}${hasDrink(bar, def.id) ? ' going' : ''}`} onClick={() => {
                  tapped.current = def.id;
                  actions.orderDrink(i);
                }}>
                <DrinkIcon key={drinking?.id === def.id ? drinking.key : 'still'} id={def.id} size={44} drinking={drinking?.id === def.id} empty={hasDrink(bar, def.id) || cooldownOf(bar, def.id) > 0} />
                <span className="item-body">
                <span className="item-top">
                  <span className="item-name">{def.name}</span>
                  <span className="item-price">{money(price)}</span>
                </span>
                <span className="item-effect">{def.effect}</span>
                <span className="item-meta">
                  <span>{def.buzz >= 0 ? `+${def.buzz}` : `−${-def.buzz}`} Buzz · {lastsLabel(def.lasts)}{def.slotless ? ' · no hand needed' : ''}</span>
                  <span className={`badge${available ? ' ready' : ''}`}>{confirming ? 'Tap again: blacks you out' : state}</span>
                </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </MenuFrame>
  );
}
